import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  "bg-amber-500", "bg-emerald-500", "bg-sky-500", "bg-violet-500",
  "bg-rose-500", "bg-lime-500", "bg-fuchsia-500", "bg-cyan-500",
];
const colorFor = (id) => {
  let h = 0;
  for (let i = 0; i < (id || "").length; i++) h = (h * 31 + (id || "").charCodeAt(i)) & 0xffff;
  return COLORS[h % COLORS.length];
};

/**
 * PresenceAvatars
 * Polling-based presence — heartbeat every 15s, refresh every 8s.
 * Renders an avatar stack of who else is currently viewing/editing the project.
 * Props: projectId, currentUserId
 */
export default function PresenceAvatars({ projectId, currentUserId }) {
  const [members, setMembers] = useState([]);
  const heartbeatRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!projectId) return undefined;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const heartbeat = async () => {
      try {
        await axios.post(`${API}/projects/${projectId}/presence/heartbeat`, {}, { headers });
      } catch (_e) { /* best-effort */ }
    };
    const refresh = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/presence`, { headers });
        setMembers(res.data?.presence || []);
      } catch (_e) { /* best-effort */ }
    };
    const leave = async () => {
      try {
        await axios.delete(`${API}/projects/${projectId}/presence`, { headers, timeout: 1500 });
      } catch (_e) { /* fire and forget */ }
    };

    // Kick off immediately then on intervals.
    heartbeat();
    refresh();
    heartbeatRef.current = setInterval(heartbeat, 15000);
    pollRef.current = setInterval(refresh, 8000);

    window.addEventListener("beforeunload", leave);
    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(pollRef.current);
      window.removeEventListener("beforeunload", leave);
      leave();
    };
  }, [projectId]);

  const others = members.filter((m) => m.user_id !== currentUserId).slice(0, 6);
  if (others.length === 0) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center -space-x-2" data-testid="presence-avatars">
        {others.map((m) => (
          <Tooltip key={m.user_id}>
            <TooltipTrigger asChild>
              <span
                className={`inline-flex items-center justify-center w-8 h-8 rounded-full ring-2 ring-slate-900 text-white text-xs font-semibold ${colorFor(m.user_id)}`}
                data-testid={`presence-avatar-${m.user_id}`}
              >
                {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-slate-800 border-slate-700 text-slate-100 text-xs">
              {m.name || m.email} · online
            </TooltipContent>
          </Tooltip>
        ))}
        {members.length - 1 > 6 && (
          <span className="ml-1 text-xs text-slate-400 pl-3">+{members.length - 1 - 6}</span>
        )}
      </div>
    </TooltipProvider>
  );
}
